/**
 * Tiny helper for storing the visitor's recent search terms locally.
 * Used to personalise the "Fresh on the market" homepage rail so
 * frequently-searched counties / towns float to the top.
 *
 * Kept intentionally small — no server round-trip, no PII sent anywhere.
 */

const KEY = 'lh_recent_searches_v1';
const MAX = 8;

/** Return the last N search entries (most recent first). */
export const readRecentSearches = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

/**
 * Push a new search entry. Duplicates (same normalised location) bubble
 * to the top instead of being repeated.
 *
 * @param {object} entry — { location, county, listingType, propertyType }
 */
export const recordSearch = (entry) => {
  try {
    if (!entry) return;
    const loc = (entry.location || '').trim();
    const county = (entry.county || '').trim();
    if (!loc && !county) return;
    const key = (loc || county).toLowerCase();
    const existing = readRecentSearches().filter(
      (e) => ((e.location || e.county || '').toLowerCase()) !== key,
    );
    const next = [
      { ...entry, location: loc, county, ts: Date.now() },
      ...existing,
    ].slice(0, MAX);
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // localStorage disabled — silently skip.
  }
};

/**
 * Score a property (0..N) against recent searches. Higher = more relevant.
 * Handles both `property.location.county` and `property.location.area`.
 */
export const relevanceScore = (property, recent) => {
  if (!property || !recent || recent.length === 0) return 0;
  const loc = property.location || {};
  const county = (loc.county || '').toLowerCase();
  const area = (loc.area || '').toLowerCase();
  const city = (loc.city || '').toLowerCase();
  const town = (loc.town || '').toLowerCase();
  let score = 0;
  recent.forEach((s, idx) => {
    // Weight recency: freshest search gets 3, older ones fade to 1.
    const weight = Math.max(1, 4 - idx);
    const needle = ((s.location || s.county || '') || '').toLowerCase();
    if (!needle) return;
    if (
      county === needle ||
      area === needle ||
      city === needle ||
      town === needle
    ) {
      score += weight * 3;
    } else if (
      county.includes(needle) ||
      area.includes(needle) ||
      city.includes(needle) ||
      needle.includes(county) ||
      needle.includes(area)
    ) {
      score += weight * 2;
    }
    if (s.propertyType && property.propertyType === s.propertyType) score += 1;
  });
  return score;
};
